import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { v4 } from 'uuid';

import { AnalyticsService } from '@libs/src/analytics/analytics.service';
import { FullscreenWindow } from '@libs/src/fullscreen-window/fullscreen-window.service';
import { IntercomService } from '@libs/src/intercom/intercom.service';
import { LoggerService } from '@libs/src/logger/logger.service';
import { CustomerInfoDialogService } from '@static-client/src/app/customer-info/customer-info-dialog.service';

import { mediaImages } from './media-logos';
import { partnerImages } from './partner-logos';
import { description, keywords } from '@static-client/src/app/app.constants';

import { AuthService } from '../auth.service';
import { WindowRefService } from '../window-ref.service';

declare let analytics: any;

const analyticsPrefix = `Landing - `;
const analyticsInteractionEvent = `${analyticsPrefix}Interaction`;
const analyticsShownEvent = `${analyticsPrefix}Shown`;
const landingVersion = '6.0';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
})
export class LandingComponent implements OnInit {
  cta = 'Chat with us';
  anonymousUserId: string;
  rootLink: any;
  isUserAuthenticated: boolean;
  mediaImages = mediaImages;
  partnerImages = partnerImages;
  tagLine = 'People learn best from people.';

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private customerInfoDialogService: CustomerInfoDialogService,
    private fullscreenWindow: FullscreenWindow,
    private fb: FormBuilder,
    private intercomService: IntercomService,
    private logger: LoggerService,
    private route: ActivatedRoute,
    private title: Title,
    private meta: Meta,
    private windowRefService: WindowRefService,
  ) {
    this.meta.addTags([
      {
        name: 'name',
        content: 'Ten Thousand Coffees',
      },
      {
        name: 'description',
        content: description,
      },
      {
        name: 'keywords',
        content: keywords,
      },
    ]);
  }

  ngOnInit() {
    this.rootLink =
      this.windowRefService.nativeWindow &&
      this.windowRefService.nativeWindow.location &&
      this.windowRefService.nativeWindow.location.host;
    this.isUserAuthenticated = this.authService.isAuthenticated();

    this.title.setTitle('Ten Thousand Coffees');

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.isUserAuthenticated) {
      const segmentAnonymousId =
        analytics && analytics.user && analytics.user().anonymousId();
      this.anonymousUserId = segmentAnonymousId || v4();
    }
    this.logInfo(analyticsShownEvent);
  }

  trackInteractionAnalytics(properties) {
    this.logInfo(analyticsInteractionEvent, properties);
  }

  reloadPage() {
    this.windowRefService.nativeWindow.location.reload();
  }

  showCustomerServiceOption() {
    if (!this.intercomService.checkIntercomStatus()) {
      this.customerInfoDialogService.open({});
    }
  }

  private logInfo(eventName: string, properties: any = {}) {
    this.logger
      .logInfo({
        message: eventName,
        properties: {
          ...properties,
          ...this.baseAnalyticsProperties,
        },
        ...(!this.isUserAuthenticated && { anonymousId: this.anonymousUserId }),
      })
      .subscribe();
  }

  get baseAnalyticsProperties() {
    return {
      tagLine: this.tagLine,
      version: landingVersion,
    };
  }
}
